import React, { useState, useEffect } from 'react';
import api from '../../api';
import Skeleton from 'react-loading-skeleton';
import LoanStatusBadge from './LoanStatusBadge';
import LoanTypeIcon from './LoanTypeIcon';
import InstallmentProgressBar from './InstallmentProgressBar';
import PaymentModal from './PaymentModal';
import PaymentPlanItemsList from './PaymentPlanItemsList';
import PaymentPlanBanner from './PaymentPlanBanner';
import PaymentPlanStatusBadge from './PaymentPlanStatusBadge';
import { formatDate } from '../../utils/dateFormatter';
import { formatCurrency } from '../../utils/formatCurrency';
import {
    DISPLAY_MODE,
    isDelinquentPaymentPlanMode,
    isOperationalPaymentPlanMode,
    isTerminalPaymentPlanMode,
    resolveDisplayMode,
} from '../../utils/paymentPlanDisplay';
import { hasPayableInstallments } from '../../utils/paymentPlanPayables';
import 'react-loading-skeleton/dist/skeleton.css';

function LoanDetailSkeleton() {
    return (
        <div className="loan-detail-skeleton">
            {/* Header Skeleton */}
            <div className="d-flex align-items-center mb-4">
                <div className="btn btn-link text-muted p-0 me-3">
                    <i className="bi bi-arrow-left icon-md"></i>
                </div>
                <Skeleton width={150} />
            </div>

            {/* Card Principal Skeleton */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-3">
                        <div className="flex-grow-1">
                            <Skeleton width={200} />
                            <Skeleton width={150} />
                        </div>
                        <Skeleton width={80} height={24} borderRadius={12} />
                    </div>
                    <div className="row g-3">
                        <div className="col-12">
                            <div className="p-3 bg-light rounded-3">
                                <Skeleton width="75%" />
                                <Skeleton width="50%" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lista de Cuotas Skeleton */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                    <Skeleton width={100} className="mb-3" />
                    <Skeleton width={250} className="mb-4" />
                    <Skeleton count={3} height={80} className="mb-3" />
                </div>
            </div>

        </div>
    );
}

function RegularInstallmentsSection({
    loan,
    installments,
    showInstallmentsDetail,
    onToggleDetail,
    isInstallmentOverdue,
    isInstallmentDueThisMonth,
    title = 'Plan de pagos',
    subtitle,
    collapsible = true,
    defaultExpanded = false,
}) {
    if (!installments?.length) {
        return null;
    }

    const resolvedSubtitle =
        subtitle ||
        `${loan.financial.installments_info.count} cuotas de ${formatCurrency(loan.financial.installments_info.amount)} ${loan.financial.installments_info.period_label.toLowerCase()}`;

    return (
        <div className="card border-0 shadow-sm mb-4">
            <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <h6 className="mb-1">{title}</h6>
                        <div className="text-muted small">{resolvedSubtitle}</div>
                    </div>
                    {collapsible && (
                        <button
                            type="button"
                            className="btn btn-link text-primary p-0"
                            onClick={onToggleDetail}
                        >
                            {showInstallmentsDetail ? (
                                <>
                                    <i className="bi bi-chevron-up me-1"></i>
                                    Ocultar
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-chevron-down me-1"></i>
                                    Ver detalle
                                </>
                            )}
                        </button>
                    )}
                </div>

                <div className="mb-3">
                    <InstallmentProgressBar
                        installments={installments}
                        totalCount={loan.financial.installments_info.count}
                    />
                </div>

                {(!collapsible || showInstallmentsDetail) && (
                    <div className="installments-list mt-3">
                        {installments.map((installment) => {
                            const isPaid = String(installment.status?.code) === '3';
                            const isOverdue = isInstallmentOverdue(installment);
                            const isDueThisMonth = isInstallmentDueThisMonth(installment);

                            return (
                                <div
                                    key={installment.number}
                                    className="d-flex align-items-center p-3 border-bottom"
                                >
                                    <div className="me-auto">
                                        <div className="d-flex align-items-center">
                                            <h6 className="mb-0">Cuota {installment.number}</h6>
                                            <small className="text-muted ms-2">
                                                <i className="bi bi-calendar3 me-1"></i>
                                                {formatDate(installment.dates.due_date)}
                                            </small>
                                        </div>
                                        {isPaid ? (
                                            <small className="text-success">
                                                <i className="bi bi-check-circle me-1"></i>
                                                Pagado el {formatDate(installment.dates.paid_date)}
                                            </small>
                                        ) : isOverdue ? (
                                            <small className="text-danger">
                                                <i className="bi bi-exclamation-circle me-1"></i>
                                                Vencida
                                            </small>
                                        ) : isDueThisMonth ? (
                                            <small className="text-warning">
                                                <i className="bi bi-calendar-check me-1"></i>
                                                Vence este mes
                                            </small>
                                        ) : (
                                            <small className="text-muted">
                                                <i className="bi bi-clock me-1"></i>
                                                Pendiente de pago
                                            </small>
                                        )}
                                    </div>
                                    <div className="text-end">
                                        <h6 className="mb-0">{formatCurrency(installment.financial.total)}</h6>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function LoanDetail({ loanId, token, onBack, client }) {
    const [loan, setLoan] = useState(null);
    const [installments, setInstallments] = useState(null);
    const [displayMode, setDisplayMode] = useState(DISPLAY_MODE.REGULAR_LOAN);
    const [paymentPlan, setPaymentPlan] = useState(null);
    const [lastPaymentPlan, setLastPaymentPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showInstallmentsDetail, setShowInstallmentsDetail] = useState(false);
    const [showOriginalInstallments, setShowOriginalInstallments] = useState(false);

    const isInstallmentOverdue = (installment) => {
        if (String(installment.status?.code) === '3') return false;
        const dueDate = installment.dates?.due_date;
        if (!dueDate) return false;

        const dueDateStr = String(dueDate).split(/[\sT]/)[0];
        const [y1, m1, d1] = dueDateStr.split('-').map(Number);

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const due = new Date(y1, m1 - 1, d1);

        return due < today;
    };

    const isInstallmentDueThisMonth = (installment) => {
        if (String(installment.status?.code) === '3') return false;
        const dueDate = installment.dates?.due_date;
        if (!dueDate) return false;

        const dueDateStr = String(dueDate).split(/[\sT]/)[0];
        const [y1, m1] = dueDateStr.split('-').map(Number);

        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        return y1 === currentYear && m1 === currentMonth;
    };

    useEffect(() => {
        const fetchLoanDetail = async () => {
            setLoading(true);
            try {
                const response = await api.get(`/c/${token}/l/${loanId}/i`, {
                    headers: { dni: localStorage.getItem(token + 'dni') }
                });

                const payload = response.data.data;
                setLoan(payload.loan);
                setInstallments(payload.installments ?? []);
                setDisplayMode(resolveDisplayMode(payload));
                setPaymentPlan(payload.payment_plan ?? null);
                setLastPaymentPlan(payload.last_payment_plan ?? null);
                setError(null);
            } catch (fetchError) {
                console.error('Error:', fetchError);
                setError('No se pudo cargar el detalle del préstamo');
            } finally {
                setLoading(false);
            }
        };

        fetchLoanDetail();
    }, [loanId, token]);

    if (loading) {
        return <LoanDetailSkeleton />;
    }

    if (error) {
        return (
            <div className="text-center p-4">
                <div className="text-danger mb-3">
                    <i className="bi bi-exclamation-circle display-4"></i>
                </div>
                <h5>{error}</h5>
                <button
                    type="button"
                    className="btn btn-outline-primary mt-3"
                    onClick={onBack}
                >
                    Volver al listado
                </button>
            </div>
        );
    }

    if (!loan) {
        return null;
    }

    const isActivePlan = isOperationalPaymentPlanMode(displayMode);
    const isDelinquentPlan = isDelinquentPaymentPlanMode(displayMode);
    const showTerminalBanner = isTerminalPaymentPlanMode(displayMode);

    const hasPendingInstallments = hasPayableInstallments({
        displayMode,
        paymentPlan,
        installments,
    });

    return (
        <div className="loan-detail">
            <div className="d-flex align-items-center mb-4">
                <button
                    type="button"
                    className="btn btn-link text-muted p-0 me-3"
                    onClick={onBack}
                >
                    <i className="bi bi-arrow-left icon-md"></i>
                </button>
                <h5 className="mb-0">Detalle del Préstamo #{loan.reference}</h5>
            </div>

            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-3">
                        <div className="d-flex align-items-center me-auto">
                            <LoanTypeIcon type={loan.type} />
                            <div>
                                <h6 className="mb-1">{loan.product}</h6>
                            </div>
                        </div>
                        <div className="d-flex flex-column align-items-end gap-1">
                            {displayMode !== DISPLAY_MODE.REGULAR_LOAN ? (
                                <PaymentPlanStatusBadge
                                    status={paymentPlan?.status ?? lastPaymentPlan?.status}
                                    displayMode={displayMode}
                                    statusLabel={paymentPlan?.status_label ?? lastPaymentPlan?.status_label}
                                />
                            ) : (
                                <LoanStatusBadge status={loan.status.code} />
                            )}
                        </div>
                    </div>

                    <div className="row g-3">
                        <div className="col-6 col-md-3">
                            <div className="text-muted small mb-1">
                                <i className="bi bi-calendar3 me-1"></i>
                                Emisión
                            </div>
                            <div className="fw-semibold">{formatDate(loan.issued_date)}</div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="text-muted small mb-1">
                                <i className="bi bi-calendar-x me-1"></i>
                                Último vto.
                            </div>
                            <div className="fw-semibold">{formatDate(loan.term.last_due_date)}</div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="text-muted small mb-1">
                                <i className="bi bi-percent me-1"></i>
                                TNA
                            </div>
                            <div className="fw-semibold">{(loan.financial.rate * 100).toFixed(1)}%</div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="text-muted small mb-1">
                                <i className="bi bi-cash-stack me-1"></i>
                                Monto
                            </div>
                            <div className="fw-semibold">{formatCurrency(loan.financial.capital)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {isDelinquentPlan && paymentPlan && (
                <PaymentPlanBanner
                    displayMode={displayMode}
                    statusLabel={paymentPlan.status_label}
                />
            )}

            {showTerminalBanner && (
                <PaymentPlanBanner
                    displayMode={displayMode}
                    lastPaymentPlan={lastPaymentPlan}
                />
            )}

            {hasPendingInstallments && client?.configuration?.allow_loan_payment && (
                <div className="mb-4">
                    <button
                        type="button"
                        className="btn btn-primary w-100 py-3 rounded-pill fw-semibold shadow"
                        onClick={() => setShowPaymentModal(true)}
                        style={{ fontSize: '1.1rem' }}
                    >
                        <i className="bi bi-credit-card me-2"></i>
                        Pagar cuotas
                    </button>
                </div>
            )}

            {isActivePlan && paymentPlan && (
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body p-4">
                        <div className="mb-3">
                            <h6 className="mb-1">Plan de pagos vigente</h6>
                            <div className="text-muted small">
                                Cumplí con los compromisos del plan para saldar la deuda de este préstamo.
                            </div>
                        </div>

                        <div className="row g-3 mb-3">
                            <div className="col-4">
                                <div className="p-2 bg-light rounded-3 text-center">
                                    <small className="text-muted d-block">Total plan</small>
                                    <strong>{formatCurrency(paymentPlan.plan_total_amount)}</strong>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="p-2 bg-light rounded-3 text-center">
                                    <small className="text-muted d-block">Pagado</small>
                                    <strong className="text-success">{formatCurrency(paymentPlan.paid_amount)}</strong>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="p-2 bg-light rounded-3 text-center">
                                    <small className="text-muted d-block">Pendiente</small>
                                    <strong className="text-primary">{formatCurrency(paymentPlan.remaining_amount)}</strong>
                                </div>
                            </div>
                        </div>

                        <PaymentPlanItemsList
                            items={paymentPlan.items}
                            title="Compromisos del plan"
                            collapsible
                            defaultExpanded
                        />
                    </div>
                </div>
            )}

            {isActivePlan && installments?.length > 0 && (
                <RegularInstallmentsSection
                    loan={loan}
                    installments={installments}
                    showInstallmentsDetail={showOriginalInstallments}
                    onToggleDetail={() => setShowOriginalInstallments(!showOriginalInstallments)}
                    isInstallmentOverdue={isInstallmentOverdue}
                    isInstallmentDueThisMonth={isInstallmentDueThisMonth}
                    title="Cuotas del préstamo original"
                    subtitle="Referencia del cronograma regular del préstamo"
                    collapsible
                    defaultExpanded={false}
                />
            )}

            {!isActivePlan && installments && (
                <RegularInstallmentsSection
                    loan={loan}
                    installments={installments}
                    showInstallmentsDetail={showInstallmentsDetail}
                    onToggleDetail={() => setShowInstallmentsDetail(!showInstallmentsDetail)}
                    isInstallmentOverdue={isInstallmentOverdue}
                    isInstallmentDueThisMonth={isInstallmentDueThisMonth}
                />
            )}

            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                loan={loan}
                installments={installments}
                token={token}
                displayMode={displayMode}
                paymentPlan={paymentPlan}
            />
        </div>
    );
}

export default LoanDetail;
